// src/hooks/useUnitActivity.js
// Records how a learner uses each unit (top-level item of a lesson), so we
// can tell "studied" from "skipped" (see src/utils/unitActivity.js).
//
// Saved in learnerProgress/<uid>, field unitActivity.<unitId>:
//   lessonId, catalogId
//   opens            — how many times the unit was opened (counter)
//   seconds          — total visible time (counter; tab hidden = paused)
//   pagesBeyondFirst — true once the learner went past the first page
//   interacted       — true once they answered / flipped / recorded …
//   firstTrackedAt / lastOpenedAt
//
// Writes are few: one when a unit is opened, one when it's left (or the tab
// is hidden/closed), and one the first time it becomes "studied".
import { useEffect, useRef, useCallback } from 'react';
import { doc, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { MAX_SECONDS_PER_STRETCH } from '../utils/unitActivity';

function write(uid, unitId, fields) {
  if (!uid || !unitId) return;
  // setDoc + merge: works even if the learnerProgress doc doesn't exist yet,
  // and only touches this unit's fields.
  setDoc(
    doc(db, 'learnerProgress', uid),
    { unitActivity: { [unitId]: fields } },
    { merge: true }
  ).catch(err => console.warn('Unit activity not saved:', err));
}

export function useUnitActivity({ uid, lessonId, catalogId, unitId, internalIndex }) {
  // Current unit's unsaved signals.
  const current = useRef(null); // { unitId, visibleSince, seconds, pagesBeyondFirst, interacted, saved: {...} }

  const visibleNow = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

  // Add the visible time since the last check to the running total.
  const collectTime = () => {
    const c = current.current;
    if (!c || c.visibleSince == null) return;
    const secs = Math.round((Date.now() - c.visibleSince) / 1000);
    c.seconds += Math.min(Math.max(secs, 0), MAX_SECONDS_PER_STRETCH);
    c.visibleSince = visibleNow() ? Date.now() : null;
  };

  // Save what's been gathered for the current unit.
  const flush = useCallback(() => {
    const c = current.current;
    if (!c) return;
    collectTime();
    const fields = {};
    if (c.seconds > 0) fields.seconds = increment(c.seconds);
    if (c.pagesBeyondFirst && !c.saved.pagesBeyondFirst) fields.pagesBeyondFirst = true;
    if (c.interacted && !c.saved.interacted) fields.interacted = true;
    if (Object.keys(fields).length === 0) return;
    write(uid, c.unitId, fields);
    c.seconds = 0;
    if (fields.pagesBeyondFirst) c.saved.pagesBeyondFirst = true;
    if (fields.interacted) c.saved.interacted = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // A new unit was opened → save the previous one, start the new one.
  useEffect(() => {
    if (!uid || !unitId) return undefined;
    current.current = {
      unitId,
      visibleSince: visibleNow() ? Date.now() : null,
      seconds: 0,
      pagesBeyondFirst: false,
      interacted: false,
      // The page number resets to 0 just after a unit changes, so for a
      // moment the new unit can show the previous unit's page. Only count
      // "past the first page" after this unit has actually been on page 0.
      seenFirstPage: internalIndex === 0,
      saved: {}
    };
    write(uid, unitId, {
      lessonId: lessonId || null,
      catalogId: catalogId || null,
      opens: increment(1),
      lastOpenedAt: serverTimestamp()
    });
    return () => flush(); // leaving this unit (or the lesson)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, unitId]);

  // Went past the first page of this unit → studied. Saved straight away.
  useEffect(() => {
    const c = current.current;
    if (!c || c.unitId !== unitId) return;
    if (internalIndex === 0) { c.seenFirstPage = true; return; }
    if (c.seenFirstPage && internalIndex > 0 && !c.pagesBeyondFirst) {
      c.pagesBeyondFirst = true;
      flush();
    }
  }, [internalIndex, unitId, flush]);

  // Pause the clock when the tab is hidden; save when it's hidden or closed
  // (phones often kill the page without any other warning).
  useEffect(() => {
    const onVisibility = () => {
      const c = current.current;
      if (!c) return;
      if (document.visibilityState === 'hidden') {
        flush();
        c.visibleSince = null;
      } else if (c.visibleSince == null) {
        c.visibleSince = Date.now();
      }
    };
    const onPageHide = () => flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [flush]);

  // Call when the learner does something in the unit (answers, flips, records).
  const noteInteraction = useCallback(() => {
    const c = current.current;
    if (!c || c.interacted) return;
    c.interacted = true;
    flush();
  }, [flush]);

  return { noteInteraction };
}